import { inject, Injectable, isDevMode } from '@angular/core';
import { EncryptedStorageService } from './encrypted-storage.service';
import { OidcEncryptedStorageService } from '../../../../../../shared/src/lib/feature-seatmap/services/oidc-encryption/oidc-encrypted-storage.service';
import { CachedStorage, StorageType } from './storage.types';

/**
 * Debug utility exposing storage inspection functions on `window.debugStorage`.
 * Only active in development mode.
 *
 * Usage (in browser console):
 *   debugStorage.show()              - Display all decrypted items (both storages)
 *   debugStorage.show('session')     - Display session storage items only
 *   debugStorage.get('key')          - Get a specific item from localStorage
 *   debugStorage.get('key','session')- Get a specific item from sessionStorage
 *   debugStorage.keys()              - List all stored keys (both storages)
 *   debugStorage.keys('session')     - List session storage keys only
 *   debugStorage.showAll()           - Show all users' storage entries
 *   debugStorage.showOidc()         - Display OIDC encrypted storage data
 *   debugStorage.raw()               - Show raw localStorage/sessionStorage entries
 *   debugStorage.status()            - Show service initialization status
 *   debugStorage.help()              - List available commands
 */
@Injectable({
  providedIn: 'root',
})
export class StorageDebugService {
  private readonly storage = inject(EncryptedStorageService);
  private readonly oidcStorage = inject(OidcEncryptedStorageService);
  private initialized = false;

  /**
   * Initialize debug utilities on window object.
   * Safe to call multiple times - only initializes once.
   */
  init(): void {
    if (this.initialized || !isDevMode()) {
      return;
    }

    (globalThis as unknown as GlobalThisWithDebugStorage).debugStorage = {
      show: (storageType?: StorageType) => this.show(storageType),
      get: (key: string, storageType?: StorageType) => this.get(key, storageType),
      keys: (storageType?: StorageType) => this.keys(storageType),
      showAll: () => this.showAll(),
      showOidc: () => this.showOidc(),
      raw: () => this.raw(),
      status: () => this.status(),
      help: () => this.help(),
    };

    console.log(
      '%c🔑 Storage Debug Utils loaded. Type `debugStorage.help()` for available commands.',
      'color: #4CAF50; font-weight: bold'
    );

    this.initialized = true;
  }

  private show(storageType?: StorageType): ShowResult | string {
    if (!this.storage.isInitialized()) {
      return 'Storage not initialized. Call initialize() first.';
    }

    if (storageType) {
      const snapshot = this.storage.getCacheSnapshot(storageType);
      console.log(
        `%c${storageType === 'session' ? 'Session' : 'Local'} Storage:`,
        'font-weight: bold'
      );
      console.table(this.formatForTable(snapshot));
      return { [storageType]: snapshot };
    }

    const localSnapshot = this.storage.getCacheSnapshot('local');
    const sessionSnapshot = this.storage.getCacheSnapshot('session');

    console.log('%cLocal Storage:', 'font-weight: bold; color: #2196F3');
    console.table(this.formatForTable(localSnapshot));
    console.log('%cSession Storage:', 'font-weight: bold; color: #FF9800');
    console.table(this.formatForTable(sessionSnapshot));

    return { local: localSnapshot, session: sessionSnapshot };
  }

  private get(key: string, storageType: StorageType = 'local'): unknown {
    if (!this.storage.isInitialized()) {
      return 'Storage not initialized. Call initialize() first.';
    }

    if (!this.storage.isKeyAllowed(key)) {
      return `Key "${key}" is not registered. Available keys: ${this.storage.getAllowedKeys().join(', ')}`;
    }

    const value = this.storage.getItem(key, storageType);
    const storageLabel = storageType === 'session' ? '[session]' : '[local]';
    console.log(`%c${storageLabel} ${key}:`, 'font-weight: bold', value);
    return value;
  }

  private keys(storageType?: StorageType): KeysResult | string {
    if (!this.storage.isInitialized()) {
      return 'Storage not initialized. Call initialize() first.';
    }

    const allowedKeys = this.storage.getAllowedKeys();

    if (storageType) {
      const snapshot = this.storage.getCacheSnapshot(storageType);
      const storedKeys = Object.keys(snapshot);
      console.log(
        `%c${storageType === 'session' ? 'Session' : 'Local'} stored keys:`,
        'font-weight: bold',
        storedKeys
      );
      console.log('%cAllowed keys:', 'color: gray', allowedKeys);
      return { [storageType]: storedKeys, allowed: allowedKeys };
    }

    const localSnapshot = this.storage.getCacheSnapshot('local');
    const sessionSnapshot = this.storage.getCacheSnapshot('session');
    const localKeys = Object.keys(localSnapshot);
    const sessionKeys = Object.keys(sessionSnapshot);

    console.log('%cLocal stored keys:', 'font-weight: bold; color: #2196F3', localKeys);
    console.log('%cSession stored keys:', 'font-weight: bold; color: #FF9800', sessionKeys);
    console.log('%cAllowed keys:', 'color: gray', allowedKeys);

    return { local: localKeys, session: sessionKeys, allowed: allowedKeys };
  }

  private showAll(): AllUsersResult {
    const seenHashes = new Set<string>();
    const users: UserStorageEntry[] = [];

    // Include current session first (even if not yet saved to localStorage)
    const currentEntry = this.buildCurrentSessionEntry();
    if (currentEntry) {
      seenHashes.add(currentEntry.fullHashedId);
      users.push(currentEntry.entry);
    }

    // Add persisted entries from localStorage
    const persistedEntries = this.buildPersistedEntries(seenHashes);
    users.push(...persistedEntries);

    this.logUsersTableWithItems(users);

    return {
      totalUsers: users.length,
      totalSize: `${(users.reduce((sum, u) => sum + u.rawBytes, 0) / 1024).toFixed(2)} KB`,
      users,
    };
  }

  private buildCurrentSessionEntry(): { entry: UserStorageEntry; fullHashedId: string } | null {
    const currentSession = this.storage.getCurrentSessionInfo();
    if (!currentSession) {
      return null;
    }

    const { userId, hashedUserId, localCacheSize, sessionCacheSize } = currentSession;
    const hasSalt = localStorage.getItem(`__encrypted_storage_salt_${hashedUserId}__`) !== null;
    const rawLocalData = localStorage.getItem(`__encrypted_storage_data_${hashedUserId}__local`);
    const rawSessionData = sessionStorage.getItem(
      `__encrypted_storage_data_${hashedUserId}__session`
    );
    const localSize = rawLocalData ? rawLocalData.length : 0;
    const sessionSize = rawSessionData ? rawSessionData.length : 0;

    return {
      fullHashedId: hashedUserId,
      entry: {
        email: userId,
        hashedUserId: hashedUserId.substring(0, 16) + '...',
        hasSalt: hasSalt ? '✓' : '-',
        localSize: localSize > 0 ? `${(localSize / 1024).toFixed(2)} KB` : '(in memory)',
        sessionSize: sessionSize > 0 ? `${(sessionSize / 1024).toFixed(2)} KB` : '(in memory)',
        rawBytes: localSize + sessionSize,
        localItems: localCacheSize,
        sessionItems: sessionCacheSize,
        current: true,
      },
    };
  }

  private buildPersistedEntries(seenHashes: Set<string>): UserStorageEntry[] {
    const entriesMap = new Map<string, UserStorageEntry>();

    this.processLocalStorageEntries(seenHashes, entriesMap);
    this.processSessionStorageEntries(seenHashes, entriesMap);

    return Array.from(entriesMap.values());
  }

  private processLocalStorageEntries(
    seenHashes: Set<string>,
    entriesMap: Map<string, UserStorageEntry>
  ): void {
    const dataPrefix = '__encrypted_storage_data_';
    const saltPrefix = '__encrypted_storage_salt_';
    const userIdMap = this.storage.getUserIdMap();

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(dataPrefix)) {
        continue;
      }

      const suffix = key.replace(dataPrefix, '');
      const hashedUserId = suffix.replace(/__local$/, '').replace(/__$/, '');
      if (seenHashes.has(hashedUserId)) {
        continue;
      }

      const rawData = localStorage.getItem(key);
      const dataSize = rawData ? rawData.length : 0;

      if (entriesMap.has(hashedUserId)) {
        const entry = entriesMap.get(hashedUserId)!;
        entry.localSize = `${(dataSize / 1024).toFixed(2)} KB`;
        entry.rawBytes += dataSize;
      } else {
        const hasSalt = localStorage.getItem(`${saltPrefix}${hashedUserId}__`) !== null;
        const email = userIdMap.get(hashedUserId) ?? '(unknown - login to reveal)';
        entriesMap.set(
          hashedUserId,
          this.createUserEntry(email, hashedUserId, hasSalt, dataSize, 0)
        );
      }
    }
  }

  private processSessionStorageEntries(
    seenHashes: Set<string>,
    entriesMap: Map<string, UserStorageEntry>
  ): void {
    const dataPrefix = '__encrypted_storage_data_';
    const saltPrefix = '__encrypted_storage_salt_';
    const userIdMap = this.storage.getUserIdMap();

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (!key?.startsWith(dataPrefix)) {
        continue;
      }

      const suffix = key.replace(dataPrefix, '');
      const hashedUserId = suffix.replace(/__session$/, '');
      if (seenHashes.has(hashedUserId)) {
        continue;
      }

      const rawData = sessionStorage.getItem(key);
      const dataSize = rawData ? rawData.length : 0;

      if (entriesMap.has(hashedUserId)) {
        const entry = entriesMap.get(hashedUserId)!;
        entry.sessionSize = `${(dataSize / 1024).toFixed(2)} KB`;
        entry.rawBytes += dataSize;
      } else {
        const hasSalt = localStorage.getItem(`${saltPrefix}${hashedUserId}__`) !== null;
        const email = userIdMap.get(hashedUserId) ?? '(unknown - login to reveal)';
        const newEntry = this.createUserEntry(email, hashedUserId, hasSalt, 0, dataSize);
        newEntry.localSize = '-';
        entriesMap.set(hashedUserId, newEntry);
      }
    }
  }

  private createUserEntry(
    email: string,
    hashedUserId: string,
    hasSalt: boolean,
    localSize: number,
    sessionSize: number
  ): UserStorageEntry {
    return {
      email,
      hashedUserId: hashedUserId.substring(0, 16) + '...',
      hasSalt: hasSalt ? '✓' : '-',
      localSize: localSize > 0 ? `${(localSize / 1024).toFixed(2)} KB` : '-',
      sessionSize: sessionSize > 0 ? `${(sessionSize / 1024).toFixed(2)} KB` : '-',
      rawBytes: localSize + sessionSize,
      localItems: 0,
      sessionItems: 0,
      current: false,
    };
  }

  private logUsersTableWithItems(users: UserStorageEntry[]): void {
    console.log('%cAll User Storage Entries:', 'font-weight: bold; font-size: 14px');
    if (users.length === 0) {
      console.log('  (no storage entries found)');
      return;
    }

    for (const user of users) {
      this.logUserEntry(user);
    }
  }

  private logUserEntry(user: UserStorageEntry): void {
    const marker = user.current ? ' ◀ active' : '';
    const localInfo = this.formatSizeInfo(user.localItems, user.localSize);
    const sessionInfo = this.formatSizeInfo(user.sessionItems, user.sessionSize);

    console.log(`%c┌─ ${user.email}${marker}`, 'color: #4CAF50; font-weight: bold');
    console.log(`│  Hash: ${user.hashedUserId} | Salt: ${user.hasSalt}`);
    console.log(`│  Local: ${localInfo} | Session: ${sessionInfo}`);

    if (user.current && this.storage.isInitialized()) {
      this.logCurrentUserStorageDetails();
    } else if (!user.current) {
      console.log(
        '%c│  (encrypted - login as this user to view items)',
        'color: gray; font-style: italic'
      );
    }
    console.log('└─');
  }

  private formatSizeInfo(itemCount: number, sizeLabel: string): string {
    return itemCount > 0 && sizeLabel === '(in memory)'
      ? `${itemCount} items in memory`
      : sizeLabel;
  }

  private logCurrentUserStorageDetails(): void {
    console.log('%c│  Local Storage:', 'color: #2196F3; font-weight: bold');
    const localSnapshot = this.storage.getCacheSnapshot('local');
    const localItems = this.formatForTable(localSnapshot);
    if (localItems.length > 0) {
      console.table(localItems);
    } else {
      console.log('│  (no local items)');
    }

    console.log('%c│  Session Storage:', 'color: #FF9800; font-weight: bold');
    const sessionSnapshot = this.storage.getCacheSnapshot('session');
    const sessionItems = this.formatForTable(sessionSnapshot);
    if (sessionItems.length > 0) {
      console.table(sessionItems);
    } else {
      console.log('│  (no session items)');
    }
  }

  private raw(): RawResult {
    const localEntries: Record<string, string> = {};
    const sessionEntries: Record<string, string> = {};
    const prefix = '__encrypted_storage_';

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(prefix)) {
        const value = localStorage.getItem(key);
        localEntries[key] = value ? `${value.substring(0, 100)}...` : '';
      }
    }

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(prefix)) {
        const value = sessionStorage.getItem(key);
        sessionEntries[key] = value ? `${value.substring(0, 100)}...` : '';
      }
    }

    console.log('%cRaw encrypted localStorage entries:', 'font-weight: bold; color: #2196F3');
    console.table(localEntries);
    console.log('%cRaw encrypted sessionStorage entries:', 'font-weight: bold; color: #FF9800');
    console.table(sessionEntries);

    return { local: localEntries, session: sessionEntries };
  }

  private showOidc(): OidcResult | string {
    if (!this.oidcStorage.isReady()) {
      return 'OIDC storage not initialized.';
    }

    const snapshot = this.oidcStorage.getCacheSnapshot();
    const keys = this.oidcStorage.getKeys();

    console.log(
      '%c🔐 OIDC Encrypted Storage:',
      'font-weight: bold; color: #9C27B0; font-size: 14px'
    );
    console.log('%cKeys:', 'font-weight: bold', keys);

    // Display each OIDC config's data
    for (const key of keys) {
      const data = snapshot[key];
      console.log(`%c\n📦 Config: ${key}`, 'font-weight: bold; color: #9C27B0');

      if (typeof data === 'object' && data !== null) {
        const oidcData = data as Record<string, unknown>;

        // Show each OIDC property
        for (const [prop, value] of Object.entries(oidcData)) {
          const displayValue = this.formatOidcValue(prop, value);
          console.log(
            `  %c${prop}:%c`,
            'color: #2196F3; font-weight: bold',
            'color: inherit',
            displayValue
          );
        }
      } else {
        console.log('  ', data);
      }
    }

    return {
      ready: true,
      keys,
      data: snapshot,
    };
  }

  private formatOidcValue(prop: string, value: unknown): unknown {
    if (value === null || value === undefined) {
      return value;
    }

    // Truncate tokens for display
    if (prop === 'authnResult' && typeof value === 'object') {
      const result = value as Record<string, unknown>;
      return {
        access_token: this.truncateToken(result['access_token']),
        id_token: this.truncateToken(result['id_token']),
        refresh_token: this.truncateToken(result['refresh_token']),
        token_type: result['token_type'],
        expires_in: result['expires_in'],
        scope: result['scope'],
      };
    }

    if (prop === 'authzData' && typeof value === 'string') {
      return this.truncateToken(value);
    }

    // For userData, show the structure
    if (prop === 'userData' && typeof value === 'object') {
      return value;
    }

    // For other objects, return as-is (but could truncate long strings)
    return value;
  }

  private truncateToken(token: unknown): string {
    if (typeof token !== 'string') {
      return String(token);
    }
    if (token.length <= 50) {
      return token;
    }
    return `${token.substring(0, 20)}...${token.substring(token.length - 20)} (${token.length} chars)`;
  }

  private status(): StorageStatus {
    const status: StorageStatus = {
      initialized: this.storage.isInitialized(),
      anonymous: this.storage.isInitialized() ? this.storage.isAnonymous() : null,
      localUnsavedChanges: this.storage.isInitialized()
        ? this.storage.hasUnsavedChanges('local')
        : null,
      sessionUnsavedChanges: this.storage.isInitialized()
        ? this.storage.hasUnsavedChanges('session')
        : null,
      localItemCount: this.storage.isInitialized()
        ? Object.keys(this.storage.getCacheSnapshot('local')).length
        : null,
      sessionItemCount: this.storage.isInitialized()
        ? Object.keys(this.storage.getCacheSnapshot('session')).length
        : null,
      allowedKeys: this.storage.getAllowedKeys().length,
    };

    console.log('%cStorage Status:', 'font-weight: bold');
    console.table(status);
    return status;
  }

  private help(): void {
    console.log(
      `
%c🔑 Storage Debug Commands:
%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  %cdebugStorage.show()%c              Display all items (both storages)
  %cdebugStorage.show('session')%c    Display session storage items only
  %cdebugStorage.show('local')%c      Display local storage items only
  %cdebugStorage.get('key')%c         Get item from localStorage
  %cdebugStorage.get('key','session')%c Get item from sessionStorage
  %cdebugStorage.keys()%c             List all keys (both storages)
  %cdebugStorage.keys('session')%c    List session storage keys only
  %cdebugStorage.showAll()%c          Show all users' storage entries
  %cdebugStorage.showOidc()%c         Show OIDC encrypted storage data
  %cdebugStorage.raw()%c              Show raw encrypted entries
  %cdebugStorage.status()%c           Show service initialization status
  %cdebugStorage.help()%c             Show this help message

%cNote: Storage must be initialized before show/get/keys work.
`,
      'color: #4CAF50; font-weight: bold; font-size: 14px',
      'color: #4CAF50',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #9C27B0; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      'color: inherit',
      'color: gray; font-style: italic'
    );
  }

  private formatForTable(snapshot: CachedStorage): TableRow[] {
    const rows: TableRow[] = [];

    for (const [key, item] of Object.entries(snapshot)) {
      const value = item?.value;
      const expiresAt = item?.expiresAt ? new Date(item.expiresAt).toISOString() : '-';
      const expired = item?.expiresAt ? Date.now() > item.expiresAt : false;

      if (Array.isArray(value)) {
        // Add a header row for the array
        rows.push({
          key: `${key} (${value.length} items)`,
          value: '───────────────',
          expiresAt,
          expired,
        });

        // Add each array item on a separate line
        value.forEach((arrayItem, index) => {
          const serialized = this.safeStringify(arrayItem);
          rows.push({
            key: `  [${index}]`,
            value: serialized.substring(0, 512) + (serialized.length > 512 ? '...' : ''),
            expiresAt: '-',
            expired: false,
          });
        });
      } else {
        const serialized = this.safeStringify(value);
        rows.push({
          key,
          value: serialized.substring(0, 512) + (serialized.length > 512 ? '...' : ''),
          expiresAt,
          expired,
        });
      }
    }

    return rows;
  }

  private safeStringify(value: unknown): string {
    try {
      return JSON.stringify(value);
    } catch {
      return '[Unserializable]';
    }
  }
}

interface DebugStorageApi {
  show: (storageType?: StorageType) => ShowResult | string;
  get: (key: string, storageType?: StorageType) => unknown;
  keys: (storageType?: StorageType) => KeysResult | string;
  showAll: () => AllUsersResult;
  showOidc: () => OidcResult | string;
  raw: () => RawResult;
  status: () => StorageStatus;
  help: () => void;
}

interface ShowResult {
  local?: CachedStorage;
  session?: CachedStorage;
}

interface KeysResult {
  local?: string[];
  session?: string[];
  allowed: readonly string[];
}

interface RawResult {
  local: Record<string, string>;
  session: Record<string, string>;
}

interface OidcResult {
  ready: boolean;
  keys: string[];
  data: Readonly<Record<string, unknown>>;
}

interface AllUsersResult {
  totalUsers: number;
  totalSize: string;
  users: UserStorageEntry[];
}

interface UserStorageEntry {
  email: string;
  hashedUserId: string;
  hasSalt: string;
  localSize: string;
  sessionSize: string;
  rawBytes: number;
  localItems: number;
  sessionItems: number;
  current: boolean;
}

interface StorageStatus {
  initialized: boolean;
  anonymous: boolean | null;
  localUnsavedChanges: boolean | null;
  sessionUnsavedChanges: boolean | null;
  localItemCount: number | null;
  sessionItemCount: number | null;
  allowedKeys: number;
}

interface TableRow {
  key: string;
  value: string;
  expiresAt: string;
  expired: boolean;
}

interface GlobalThisWithDebugStorage {
  debugStorage: DebugStorageApi;
}
